import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SendIcon from '@mui/icons-material/Send';
import api from '../../services/api';

const NotificationsPage: React.FC = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ sent: number; failed: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleSend = async () => {
    if (!title.trim()) {
      setError('Please enter a notification title');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await api.post('/push/broadcast', {
        title: title.trim(),
        body: body.trim(),
        url: url.trim() || undefined,
      });

      setResult({
        sent: response.data.sent,
        failed: response.data.failed,
      });
      setSnackbarOpen(true);

      // Clear form on success
      if (response.data.sent > 0) {
        setTitle('');
        setBody('');
        setUrl('');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send notifications');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        Send Notifications
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Send push notifications to all registered families
      </Typography>

      <Card sx={{ maxWidth: 600 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <NotificationsActiveIcon sx={{ color: '#00b7ed', fontSize: 28 }} />
            <Typography variant="h6" fontWeight={600}>
              Compose Notification
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., New Event Available!"
            sx={{ mb: 2 }}
            required
            inputProps={{ maxLength: 100 }}
            helperText={`${title.length}/100 characters`}
          />

          <TextField
            fullWidth
            label="Message"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="e.g., Check out our upcoming summer camp event..."
            multiline
            rows={3}
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 200 }}
            helperText={`${body.length}/200 characters`}
          />

          <TextField
            fullWidth
            label="Link (optional)"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="e.g., /events or /events/123"
            sx={{ mb: 3 }}
            helperText="Where to navigate when notification is clicked"
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {result && (
            <Alert severity={result.sent > 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {result.sent > 0
                ? `Successfully sent to ${result.sent} device${result.sent !== 1 ? 's' : ''}`
                : 'No devices to send to'}
              {result.failed > 0 && ` (${result.failed} failed)`}
            </Alert>
          )}

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleSend}
            disabled={loading || !title.trim()}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            sx={{
              bgcolor: '#00b7ed',
              '&:hover': { bgcolor: '#0095c2' },
              py: 1.5,
            }}
          >
            {loading ? 'Sending...' : 'Send to All Families'}
          </Button>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
            This will send a push notification to all families who have enabled notifications on their devices.
          </Typography>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={result ? `Notification sent to ${result.sent} device${result.sent !== 1 ? 's' : ''}` : ''}
      />
    </Box>
  );
};

export default NotificationsPage;
